import type { CompareData } from '../Compare.types';

type Translate = (key: string) => string;

export const getDnbCompareData = (t: Translate): CompareData => ({
  competitor: 'Dun & Bradstreet',
  competitorHeader: 'D&B',
  competitorLogo: { type: 'logo-dnb', label: 'Dun & Bradstreet' },
  heroTitle: 'Global Database vs Dun & Bradstreet',
  heroDescription: (
    <>
      {t('D&B is fundamentally a')} <b>{t('data aggregator')}</b>{' '}
      {t('built around its D-U-N-S identifier system, licensing and blending data across a wide vendor network — with')}{' '}
      <b>{t('strict reseller rights')}</b>{' '}
      {t(
        'that limit how far its data can travel downstream. Global Database sources every record directly, publishes the source, link, timestamp, and official filing behind it, and adds transfer pricing, M&A history, and an ownership graph connecting 45M+ companies and 500M+ people.',
      )}
    </>
  ),
  quickItems: [
    <>
      <b>{t('Source, link, timestamp & filing')}</b> {t('published for every record — not a blended vendor feed')}
    </>,
    <>
      <b>{t('Flexible reseller rights')}</b> {t('— no strict resale lock-in on enterprise plans')}
    </>,
    <>
      <b>{t('Transfer pricing & M&A history')}</b> {t('included as standard')}
    </>,
    <>
      <b>{t('45M+ companies, 500M+ people')}</b> {t('mapped in one global ownership graph')}
    </>,
  ],

  table: {
    title: t('Global Database vs Dun & Bradstreet at a glance'),
    description: t(
      "D&B's business model centres on its proprietary D-U-N-S identifier and a wide network of licensed data partners, with tightly controlled resale terms. Global Database sources directly and shows its work.",
    ),
    rows: [
      {
        attribute: t('Data sourcing model'),
        gd: t('Direct sourcing, with source, link, timestamp and official filing published per record'),
        them: t('Data aggregator — blends and licenses data across a broad third-party vendor network'),
      },
      {
        attribute: t('Reseller rights'),
        gd: t('Flexible — enterprise clients can integrate, resell, or build derivative products'),
        them: t('Strict — resale and redistribution are tightly controlled under D&B licensing terms'),
      },
      {
        attribute: t('Financials'),
        gd: t('Digitized via OCR + AI from filed statements, mapped consistently across countries'),
        them: t('Aggregated from licensed vendor sources; consistency varies by market'),
      },
      {
        attribute: t('Ownership graph'),
        gd: t('45M+ companies and 500M+ people connected in one global graph'),
        them: t('Ownership linkage built around the D-U-N-S hierarchy and licensed data'),
      },
      {
        attribute: t('Transfer pricing'),
        gd: t('Included'),
        them: t('Available via separate D&B risk/analytics products'),
      },
      {
        attribute: t('M&A & deal history'),
        gd: t('Included'),
        them: t('Available via separate D&B products/add-ons'),
      },
      {
        attribute: t('Bank account verification'),
        gd: t('Included'),
        them: t('Not a core part of the standard offering'),
      },
    ],
  },

  stats: {
    title: t('Why sourcing model matters'),
    description: t(
      'An aggregator is only as current and traceable as its weakest upstream vendor. Direct sourcing means every fact can be traced back to where it came from.',
    ),
    items: [
      { value: '45M+', label: t('Companies in the ownership graph') },
      { value: '500M+', label: t('People connected to companies') },
      { value: '20 yrs', label: t('Digitized financial history') },
      { value: '3–5/mo', label: t('New jurisdictions added') },
    ],
    note: t(
      "Every Global Database record carries its source, a link to it, a timestamp, and — where applicable — the official filing it was drawn from. That's a structurally different guarantee than a licensed, blended feed built around a proprietary identifier: it means a credit or risk decision can point back to a primary document, not a vendor's aggregation of one.",
    ),
  },

  difference: {
    title: t("What's the difference between Global Database and Dun & Bradstreet?"),
    paragraphs: [
      <>
        <b>D&amp;B</b>{' '}
        {t(
          'is, at its core, a data aggregator built around its proprietary D-U-N-S identifier — a widely adopted business identifier, but one that sits on top of data licensed and blended from a broad vendor network. That model comes with a well-known trade-off:',
        )}{' '}
        <b>{t('reseller rights are strict')}</b>.{' '}
        {t(
          'Redistributing or building products on top of D&B data typically requires specific, tightly controlled licensing terms.',
        )}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          "takes a different starting point: data is sourced directly, with the source, link, timestamp, and official filing published against each record. On top of that direct-sourced layer, Global Database adds transfer pricing data, M&A and deal history, bank account verification, and a global ownership graph spanning 45M+ companies and 500M+ people — with flexible reseller rights for enterprise clients who want to build on top of it, rather than D&B's tightly governed licensing model.",
        )}
      </>,
      <>
        {t(
          "For organisations already standardised on the D-U-N-S number for internal matching, D&B's ecosystem has real switching costs. Where flexible resale rights, direct-to-source provenance, or transfer pricing and deal history in one platform is the requirement, that's where Global Database is built to lead.",
        )}
      </>,
    ],
    pull: t(
      '"A proprietary identifier is useful for matching records across a vendor network. It doesn\'t, on its own, tell you how current the underlying filing is — or whether you\'re free to build on top of the data."',
    ),
  },

  features: {
    title: t('Key features compared'),
    description: t("A closer look at what's included on each platform."),
    gd: [
      {
        title: t('Direct sourcing, fully traceable'),
        text: t('Source, link, timestamp and official filing published for every record.'),
      },
      {
        title: t('Flexible reseller rights'),
        text: t('Enterprise clients can integrate, resell, or build derivative products on the data.'),
      },
      {
        title: t('OCR + AI digitized financials'),
        text: t('Financial statements extracted and mapped consistently across countries.'),
      },
      {
        title: t('45M+ company / 500M+ people ownership graph'),
        text: t('A single graph connecting individuals to companies at global scale.'),
      },
      {
        title: t('Transfer pricing'),
        badge: t('Included'),
        text: t('Transfer pricing data included as part of the standard record, not a separate purchase.'),
      },
      {
        title: t('M&A & deal history'),
        text: t('Transaction history included as part of the core company record.'),
      },
      {
        title: t('Bank account verification'),
        text: t('Verification included as part of the standard data layer.'),
      },
    ],
    them: [
      {
        title: t('D-U-N-S identifier network'),
        text: t('Widely adopted proprietary identifier used to match records across a large licensed vendor network.'),
      },
      {
        title: t('Strict reseller rights'),
        text: t('Redistribution and resale of D&B data are tightly controlled under its licensing terms.'),
      },
      {
        title: t('Established risk analytics'),
        text: t('Mature credit-risk scoring and monitoring products built on aggregated data.'),
      },
      {
        title: t('Ownership data via licensed sources'),
        text: t("Corporate hierarchy assembled from D&B's own network of data partners."),
      },
      {
        title: t('Separate product lines'),
        text: t('Transfer pricing, M&A, and other capabilities often sold as distinct D&B products.'),
      },
      {
        title: t('Broad enterprise adoption'),
        text: t('Long-standing use in procurement and vendor master-data systems worldwide.'),
      },
    ],
  },

  extra: {
    id: 'reseller',
    title: t('Reseller rights compared'),
    description: (
      <em>
        {t(
          'Licensing terms vary by contract and use case — confirm specifics directly with either provider. The structural difference is below.',
        )}
      </em>
    ),
    paragraphs: [
      <>
        <b>D&amp;B</b>,{' '}
        {t(
          'as an aggregator built around the D-U-N-S identifier, licenses much of its underlying data from third-party vendors — which means resale and redistribution of D&B data are governed by strict, tightly controlled licensing terms rather than a simple usage policy.',
        )}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          "offers flexible reseller rights on enterprise plans: clients can integrate the data into their own product, resell it, or build derivative products directly on top of it, since the data is sourced and owned by Global Database's own pipeline rather than re-licensed from elsewhere.",
        )}
      </>,
    ],
  },

  faq: {
    title: t('FAQs about Global Database vs Dun & Bradstreet'),
    data: [
      {
        id: 'is-dnb-a-data-aggregator',
        default_opened: true,
        question: t('Is D&B a data aggregator?'),
        answer: t(
          "D&B's business is built around its proprietary D-U-N-S identifier, with underlying company data licensed and blended from a broad network of third-party vendors. This gives it wide adoption for record-matching, but the freshness and provenance of any given record depend on the underlying vendor it was licensed from.",
        ),
      },
      {
        id: 'can-i-resell-dnb-data',
        question: t('Can I resell D&B data in my own product?'),
        answer: t(
          'D&B is known for strict reseller rights — redistribution and resale of its data are tightly controlled under its licensing terms and typically require specific contractual approval. Global Database offers flexible reseller rights as standard on enterprise plans, since its data is sourced directly rather than re-licensed.',
        ),
      },
      {
        id: 'does-gdb-offer-transfer-pricing',
        question: t('Does Global Database offer transfer pricing data like D&B?'),
        answer: t(
          "Yes — transfer pricing data is included as part of Global Database's standard company record, alongside digitized financials and the ownership graph, rather than sold as a separate product line as it typically is with D&B.",
        ),
      },
      {
        id: 'does-gdb-include-ma',
        question: t('Does Global Database include M&A and deal history?'),
        answer: t(
          'Yes, M&A and deal history are included as part of the core company record, alongside transfer pricing and bank account verification.',
        ),
      },
      {
        id: 'what-is-the-ownership-graph',
        question: t('What is the ownership graph and how large is it?'),
        answer: t(
          "Global Database's ownership graph connects more than 45 million companies and 500 million people, mapping who owns and controls what across jurisdictions in a single connected structure.",
        ),
      },
      {
        id: 'is-gdb-a-good-dnb-alternative',
        question: t('Is Global Database a good D&B alternative for compliance and risk teams?'),
        answer: t(
          "For teams that need directly-sourced, traceable data — with source, link, timestamp, and filing published per record — plus flexible reseller rights, Global Database is commonly evaluated as a strong alternative to a strictly licensed aggregator model like D&B's.",
        ),
      },
    ],
  },

  cta: {
    title: t('See Global Database on your own company records'),
    description: t(
      "Request a data review and compare sourcing, ownership resolution, and reseller terms directly against what you're using today.",
    ),
  },
});
